import { useEffect, useState } from "react";
import {
  FetchError,
  Header,
  LoadingOverlay,
  TopicFocusTile,
} from "components/molecules";
import {
  ACT_TEST_TYPE,
  GMAT_TEST_TYPE,
  GRE_TEST_TYPE,
  MOBILE_WIDTH,
  SAT_TEST_TYPE,
  TAB_LARGE_WIDTH,
} from "utils/constants";
import { useAppSelector, useScreenSize } from "hooks";
import { Button, Typography } from "components/atoms";
import { Spinner } from "components/atoms/Icon";
import { useLazyGetTopicFocusQuery } from "services/topicFocus";
import { LiveOnlineFilterButtonSection } from "components/organisms/LiveOnlineFilterButtonSection";
import { getTimeFilterInput } from "utils/functions/getTimeFilterInput";
import {
  replaceArrayOfObjects,
  reStructureSegment,
} from "utils/functions/courseWorkReStructure";
import { getCategoryFilterInput } from "utils/functions/getCategoryFilterInput";
import { useGetOverallProgressQuery } from "services/homeApi";
import { decodeToken } from "utils/functions/decodeToken";
import { getOfficeHoursDisplayName } from "utils/functions/getOfficeHoursDisplayName";
import "./index.scss";

interface IProps {
  theme: "light" | "dark";
}

interface SelectedBtnState {
  [key: string]: boolean;
  "1": boolean;
  "2": boolean;
  "3": boolean;
}

interface ITopicFocusRequest {
  page: number;
  selectedCategory?: any;
  selectedSubjectAndTopic?: any;
  selectedTopic?: any;
  selectedSession?: any;
}

export const SelfPacedLiveOnlineSession = ({ theme }: IProps) => {
  const screen = useScreenSize();
  const payload = decodeToken();
  const { isIframeRefreshing } = useAppSelector(
    (state: { layout: { isIframeRefreshing: boolean } }) => state.layout
  );
  const [selectedBtn, setSelectedBtn] = useState<SelectedBtnState>({
    "1": true,
    "2": false,
    "3": false,
  });
  const [selectedSession, setSelectedSession] = useState<
    null | string | { [key: number]: string }
  >({ 0: "Recent and Upcoming Sessions" });

  const [selectedCategory, setSelectedCategory] = useState<
    null | string | { [key: number]: string }
  >({ 0: "Required Topic Focus" });

  const [selectedSubjectAndTopic, setSelectedSubjectAndTopic] = useState<
    | undefined
    | {
        group: string;
        label: string;
      }
  >(undefined);

  const [selectedTopic, setSelectedTopic] = useState<any>(undefined);

  const [page, setPage] = useState<number>(1);

  const [data, setData] = useState<any>([]);

  const [isSearchBtnClicked, setIsSearchBtnClicked] = useState(false);

  const userId = payload ? payload.userid : null;
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;

  const testType = payload ? payload.testtype : null;

  const [
    triggerGetTopicFocus,
    { isLoading, isFetching, isError, data: remoteData },
  ] = useLazyGetTopicFocusQuery();

  const { isFetching: isFetchingOverallProgress } = useGetOverallProgressQuery({
    Userid: userId,
    Productcode: productCode,
    Offeringcode: offeringCode,
  });

  const compareArrayOfObjects = (fetchArray: any, existArray: any) => {
    let isNewData = true;
    for (let segment of existArray) {
      if (
        fetchArray[0]?.ProductOfferingSegmentId ===
        segment?.ProductOfferingSegmentId
      ) {
        isNewData = false;
      }
    }
    return isNewData;
  };

  const handleSelectedContentChange = (setPage: any) => {
    return (contentState: any) => {
      setSelectedBtn(contentState);
    };
  };

  const handleDropDownSelection = () => {
    return (session: any) => {
      setSelectedSession(session);

      if (payload?.testtype === GMAT_TEST_TYPE) {
        setPage(1);
        handleSearchOnclick({ page: 1, selectedSession: session });
      }
    };
  };

  const handleSearchOnclick = (topicFocusRequest: ITopicFocusRequest) => {
    triggerGetTopicFocus({
      page: topicFocusRequest.page,
      ...(payload?.testtype === GMAT_TEST_TYPE
        ? {}
        : topicFocusRequest.selectedCategory !== undefined
        ? {
            selectedOption:
              topicFocusRequest.selectedCategory === null
                ? 1
                : getCategoryFilterInput(
                    Object.values(topicFocusRequest.selectedCategory)[0]
                  ),
          }
        : {}),
      ...(topicFocusRequest.selectedSubjectAndTopic !== "Show all"
        ? { subject: topicFocusRequest.selectedSubjectAndTopic }
        : {}),
      ...(topicFocusRequest.selectedTopic !== undefined &&
      topicFocusRequest.selectedSubjectAndTopic !== "Show all"
        ? { topic: topicFocusRequest.selectedTopic }
        : {}),
      ...(topicFocusRequest.selectedSession !== undefined
        ? {
            timeFilter:
              topicFocusRequest.selectedSession === null
                ? 1
                : getTimeFilterInput(
                    Object.values(topicFocusRequest.selectedSession)[0]
                  ),
          }
        : {}),
    });
  };

  const performSearchAccordingtoTesttype = (testType: string) => {
    switch (testType) {
      case ACT_TEST_TYPE:
      case SAT_TEST_TYPE:
      case GRE_TEST_TYPE:
        handleSearchOnclick({
          page: page,
          selectedSubjectAndTopic: selectedSubjectAndTopic,
          selectedTopic: selectedTopic,
          selectedSession: selectedSession,
        });
        break;

      case GMAT_TEST_TYPE:
        handleSearchOnclick({
          page: page,
          selectedSession: selectedSession,
        });
        break;

      default:
        handleSearchOnclick({
          page: page,
          selectedCategory: selectedCategory,
          selectedSubjectAndTopic: selectedSubjectAndTopic,
          selectedTopic: selectedTopic,
          selectedSession: selectedSession,
        });
        break;
    }
  };

  const matchFirstParaAccrdingtoTestype = (testType: string) => {
    switch (testType) {
      case ACT_TEST_TYPE:
      case SAT_TEST_TYPE:
        return "Enhance your test prep journey with additional sessions in our virtual classroom. Browse the schedule of upcoming sessions or use the filter to find the next offering of a specifc session.";
      case GMAT_TEST_TYPE:
        return "Enhance your preparation with additional sessions in our virtual classroom. Browse the schedule of upcoming sessions below.";
      default:
        return "Enhance your test prep journey with additional sessions in our virtual classroom. Browse the schedule of upcoming sessions or use the filter to find the next offering of a specifc session.";
    }
  };

  useEffect(() => {
    if (remoteData && remoteData.Segments && page === 1) {
      const deStructuredArray = reStructureSegment(remoteData);
      setData([...deStructuredArray]);
    } else if (
      remoteData &&
      remoteData.Segments &&
      Array.isArray(remoteData.Segments) &&
      compareArrayOfObjects(remoteData.Segments, data)
    ) {
      const deStructuredArray = reStructureSegment(remoteData);
      setData((prev: any) => [...prev, ...deStructuredArray]);
    } else if (
      remoteData &&
      remoteData.Segments &&
      Array.isArray(remoteData.Segments)
    ) {
      const updatedArray = replaceArrayOfObjects(remoteData.Segments, data);
      setData([...updatedArray]);
    }
  }, [remoteData, page]);

  useEffect(() => {
    if (isSearchBtnClicked && !isFetching) {
      setIsSearchBtnClicked(false);
    }
  }, [isFetching]);

  useEffect(() => {
    // perform refetching when page changes by clicking "load more segments" after initial component mount
    if (page !== 1) {
      performSearchAccordingtoTesttype(testType);
    }
  }, [page]);

  useEffect(() => {
    // perform fetch data on initial component mount for GMAT_TEST_TYPE test type
    if (testType === GMAT_TEST_TYPE && page === 1) {
      performSearchAccordingtoTesttype(testType);
    }
  }, []);

  useEffect(() => {
    // perform fetch data on initial component mount when search button available
    if (!isSearchBtnClicked && page === 1 && testType !== GMAT_TEST_TYPE) {
      performSearchAccordingtoTesttype(testType);
    }
  }, []);

  return (
    <div className="live-online-container">
      <div className="live-online-header-container">
        <div className="header-section">
          <Header
            theme={theme}
            leftContentLabel={
              payload?.testtype === GMAT_TEST_TYPE
                ? getOfficeHoursDisplayName(payload?.testtype || "")
                : `${getOfficeHoursDisplayName(payload?.testtype || "")} Sessions`
            }
            leftContentStyle="header-custom-style"
            leftContentTypographyStyle="left-content-typography-style"
            isTab={screen.width <= TAB_LARGE_WIDTH}
            customVariant={screen.width <= MOBILE_WIDTH ? "h2Bold" : "h3Bold"}
          />
        </div>
      </div>
      <div className="header-description">
        <Typography
          variant="h8Book"
          label={`${matchFirstParaAccrdingtoTestype(testType)}`}
        />
        <Typography
          variant="h8Book"
          label="Whether attending or reviewing the recording at a later
                    time, click “Add To Coursework” for any sessions that interest you. The sessions will
                    appear on your homepage and the classroom launch link will become available only minutes
                    before the session starts, so be sure to refresh your page. You will have this option as
                    long as your online access is active, and The Princeton Review will extend your access as
                    necessary to make sure you can prep until your test date arrives."
        />
        <Typography
          variant="h8Book"
          label={
            <>
              <span className="note-span-style">NOTE: </span>Your class link
              will appear 15 minutes before your class begins. Please refresh
              your browser within 15 minutes before the start of your class
              session.
            </>
          }
        />
      </div>
      <LoadingOverlay theme={theme} minDisplayMs={800} isLoading={!isIframeRefreshing && isFetchingOverallProgress}>
        <LiveOnlineFilterButtonSection
          setDropDownSelection={handleDropDownSelection()}
          dropdownSelection={selectedSession}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          selectedSubjectAndTopic={selectedSubjectAndTopic}
          setSelectedSubjectAndTopic={setSelectedSubjectAndTopic}
          setSelectedTopic={setSelectedTopic}
          selectedTopic={selectedTopic}
          theme={theme}
          data={remoteData}
          onClickSearch={handleSearchOnclick}
          setPage={setPage}
          setIsSearchBtnClicked={setIsSearchBtnClicked}
          isFetchingData={isFetching && isSearchBtnClicked}
        />
        <div className="card-wrapper">
          {isLoading ? (
            <Spinner theme={theme} />
          ) : isError ? (
            <FetchError content="Oops! failed to load resources." />
          ) : (
            data &&
            Array.isArray(data) &&
            data.map((item: any, index: number) => (
              <TopicFocusTile
                key={index}
                borderColor={"pink"}
                isTabShow={false}
                btnLabel={
                  item.IsAddedToHome
                    ? "Remove from Coursework"
                    : "Add to Course Work"
                }
                btnType={item.IsAddedToHome ? "active" : "score"}
                title={item.Description}
                startTime={item.StarttimeUTC}
                endTime={item.EndtimeUTC}
                profileImage={item.InstructorPhotoUrl}
                name={item.InstructorName}
                theme={theme}
                segmentId={item.ProductOfferingSegmentId}
                instructorId={item.InstructorMsmId}
                isAddedToHome={item.IsAddedToHome}
                btnUrl={item.ClassUrl}
                index={index}
                page={item.page}
                setData={setData}
                isFetchingOtherData={isFetching}
                data={data}
                selectedSession={selectedSession}
                selectedCategory={selectedCategory}
                selectedSubject={selectedSubjectAndTopic}
                selectedTopic={selectedTopic}
              />
            ))
          )}
        </div>
        {isLoading ? null : (
          <div className="bottom-btn-section">
            <Button
              icon={false}
              type="score"
              label="Load More Segments"
              onClick={() => setPage((prev) => prev + 1)}
              isLoading={isFetching}
              isDisabled={remoteData?.Total === data.length}
            />
          </div>
        )}
      </LoadingOverlay>
    </div>
  );
};
